import { MouseEvent, useState, useEffect, useCallback } from 'react';

import useMediaQuery from '@sureapp/wl-toggle-common/dist/hooks/useMediaQuery';
import {
  disableBodyScroll,
  enableBodyScroll,
} from '@sureapp/wl-toggle-common/dist/utils/dom';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';

import { getIsCcpaBannerOpen } from '@@/auto/store/selectors';
import Logo from '@@/common/components/Logo';
import TenantPhoneLink from '@@/common/components/TenantPhoneLink';
import useIsLoggedIn from '@@/common/hooks/useIsLoggedIn';
import { useTimeout } from '@@/common/hooks/useTimeout';

import classes from './NavBar.module.css';
import NavBarMenu from './NavBarMenu';
import { useNavLinks } from './useNavLinks';

export type TNavBar = {
  className?: string;
  isHideNavigation?: boolean;
  overrideClasses?: {
    root?: string;
    mobile_nav_cover?: string;
    logo?: string;
    navbar_temp?: string;
    navbarContainer?: string;
  };
};

function NavBar({
  className = '',
  overrideClasses = {},
  isHideNavigation = false,
}: TNavBar) {
  const { setTimeoutSafe } = useTimeout();
  const navLinks = useNavLinks();
  const isLoggedIn = useIsLoggedIn();
  const isCcpaBannerOpen = useSelector(getIsCcpaBannerOpen);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuClosing, setIsMenuClosing] = useState(false);
  const homeUrl = isLoggedIn ? '/my-account' : '/';
  const isDesktop = useMediaQuery('(min-width: 936px)');

  useEffect(() => {
    if (isMenuOpen) {
      disableBodyScroll();
    } else {
      enableBodyScroll();
    }
    return enableBodyScroll;
  }, [isMenuOpen]);

  const handleToggleMenu = useCallback(() => {
    const screenWidth = Math.max(
      document.body.scrollWidth,
      document.documentElement.scrollWidth,
      document.body.offsetWidth,
      document.documentElement.offsetWidth,
      document.documentElement.clientWidth
    );

    function handleCloseMenu() {
      setIsMenuOpen(true);
      setIsMenuClosing(true);
      setTimeoutSafe(() => {
        setIsMenuOpen(false);
        setIsMenuClosing(false);
      }, 400);
    }

    if (screenWidth >= 936) {
      if (isMenuOpen) {
        handleCloseMenu();
      }
      return;
    }

    if (!isMenuClosing) {
      if (isMenuOpen) {
        handleCloseMenu();
      } else {
        setIsMenuOpen(true);
      }
    }
  }, [isMenuClosing, isMenuOpen, setTimeoutSafe]);

  useEffect(
    function toggleMenuOnScreenSizeChange() {
      if (isDesktop) {
        handleToggleMenu();
      }
    },
    [handleToggleMenu, isDesktop]
  );

  function handleClick(e: MouseEvent<HTMLAnchorElement>) {
    if (e.currentTarget.href === window.location.href) {
      e.preventDefault();
      handleToggleMenu();
    }
  }

  const openCloseClasses = clsx({
    'is-open': isMenuOpen,
    [classes.is_open]: isMenuOpen,
    [classes.is_closing]: isMenuClosing,
  });

  const navBarClass = clsx(classes.root, className, openCloseClasses);

  const mobileNavCoverClass = clsx(
    classes.mobile_nav_cover,
    overrideClasses.mobile_nav_cover,
    openCloseClasses
  );

  return (
    <nav>
      {(isMenuOpen || isMenuClosing) && (
        <div
          className={clsx(
            classes.nav_bar,
            classes.navbar_temp,
            openCloseClasses,
            overrideClasses.navbar_temp
          )}
        >
          <NavLink
            to={homeUrl}
            className={clsx(classes.logo, overrideClasses.logo)}
            onClick={handleClick}
            data-testid="tenant_logo"
          >
            <Logo
              disableClick
              isLight={!(isMenuOpen && !isMenuClosing)}
              overrideClasses={{ root: classes.logo_img }}
            />
          </NavLink>
        </div>
      )}
      <div
        className={clsx(
          classes.wrapper,
          classes.nav_bar,
          {
            has_open_ccpa_banner: isCcpaBannerOpen,
          },
          openCloseClasses,
          overrideClasses.root
        )}
        data-testid="navbar"
      >
        <div className={mobileNavCoverClass} />
        <div className={clsx(navBarClass, openCloseClasses)}>
          <div
            className={clsx(
              classes.container,
              openCloseClasses,
              overrideClasses?.navbarContainer
            )}
          >
            <NavLink
              to={homeUrl}
              className={clsx(classes.logo, overrideClasses.logo)}
              onClick={handleClick}
              data-testid="tenant_logo"
            >
              <Logo
                disableClick
                isLight={!(isMenuOpen && !isMenuClosing)}
                overrideClasses={{ root: classes.logo_img }}
              />
            </NavLink>
            {!isHideNavigation && navLinks && (
              <>
                <NavBarMenu
                  links={navLinks}
                  onToggleMenu={handleToggleMenu}
                  phoneLink={
                    <TenantPhoneLink className={classes.tenant_phone} />
                  }
                />
                <button
                  className={clsx(classes.hamburger_btn, 'icon_btn')}
                  aria-label={
                    isMenuOpen
                      ? 'Close primary navigation menu'
                      : 'Open primary navigation menu'
                  }
                  onClick={handleToggleMenu}
                  type="button"
                >
                  <div
                    data-testid="navBar_menuIcon"
                    className={classes.hamburger_icon_wrapper}
                  >
                    <div className={classes.hamburger_icon} />
                  </div>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default NavBar;
